// Cac manh giao dien dung lai nhieu cho. Khong co logic nghiep vu o day.

import type { ReactNode } from "react";
import { useEffect } from "react";
import type { QuyetDinh, RunStatus } from "../types";

export function Spinner() {
  return <span className="spinner" aria-label="đang tải" />;
}

export function Badge({
  children,
  tone = "default",
  title,
}: {
  children: ReactNode;
  tone?: "default" | "ok" | "warn" | "danger" | "info" | "accent";
  title?: string;
}) {
  const cls = tone === "default" ? "badge" : `badge badge-${tone}`;
  return (
    <span className={cls} title={title}>
      {children}
    </span>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="track" />
      {label ? <span className="switch-label">{label}</span> : null}
    </label>
  );
}

export function Alert({
  tone = "info",
  children,
}: {
  tone?: "info" | "ok" | "warn" | "danger";
  children: ReactNode;
}) {
  const ico = { info: "i", ok: "✓", warn: "!", danger: "×" }[tone];
  return (
    <div className={`alert alert-${tone}`}>
      <strong aria-hidden>{ico}</strong>
      <div className="grow">{children}</div>
    </div>
  );
}

export function Empty({ icon = "○", children }: { icon?: string; children: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-ico">{icon}</div>
      <div>{children}</div>
    </div>
  );
}

export function Modal({
  title,
  children,
  footer,
  onClose,
  width,
}: {
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  width?: number;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={width ? { width: `min(${width}px, 100%)` } : undefined}>
        <div className="modal-head">
          <h2 className="grow">{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Đóng">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

const TRANG_THAI: Record<RunStatus, { nhan: string; tone: "ok" | "warn" | "danger" | "info" }> = {
  running: { nhan: "Đang chạy", tone: "info" },
  cancelling: { nhan: "Đang dừng", tone: "warn" },
  success: { nhan: "Thành công", tone: "ok" },
  failed: { nhan: "Thất bại", tone: "danger" },
  cancelled: { nhan: "Đã dừng", tone: "warn" },
  error: { nhan: "Lỗi", tone: "danger" },
};

export function StatusBadge({ status }: { status: RunStatus }) {
  const s = TRANG_THAI[status] ?? { nhan: status, tone: "info" as const };
  const dangChay = status === "running" || status === "cancelling";
  return (
    <Badge tone={s.tone}>
      <span className={dangChay ? "dot dot-pulse" : "dot"} />
      {s.nhan}
    </Badge>
  );
}

/** Nhan cho ket luan cua bo may quyet dinh trong quyet_dinh.py. */
export function QuyetDinhBadge({ qd, diem }: { qd: QuyetDinh; diem?: number | null }) {
  if (!qd) return null;
  const diemTxt = diem == null ? "" : ` ${diem}đ`;
  if (qd === "tu_dong")
    return <Badge tone="ok" title="Đủ điểm để tự động điền">Tự động{diemTxt}</Badge>;
  if (qd === "can_duyet")
    return <Badge tone="warn" title="Cần người duyệt trước khi ghi">Cần duyệt{diemTxt}</Badge>;
  return <Badge tone="danger" title="Không đủ bằng chứng, đã loại">Loại bỏ{diemTxt}</Badge>;
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <Alert tone="danger">{msg}</Alert>;
}
